import { Avatar } from "../../core/Avatar.js";
import { Button } from "../../core/Button.js";
import { Modal, ModalClose, ModalFooter } from "../../core/Modal.js";
import { Text } from "../../core/Text.js";

export interface ChatIgnoredUser {
  id: string;
  name: string;
}

export interface ChatIgnoredModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Ignored players". */
  title: string;
  /** What ignoring does: "Their messages are hidden on this device only. They are not told." */
  description?: string;
  users: readonly ChatIgnoredUser[];
  /** Shown when the list is empty: "You are not ignoring anyone." */
  emptyLabel: string;
  /** The button on each row: "Unignore". */
  unignoreLabel: string;
  /** Each row button's full name: `Unignore ${name}`. */
  unignoreName: (name: string) => string;
  onUnignore: (id: string) => void;
  doneLabel: string;
}

/** The ignored players as rows, each with its Unignore; the modal's body. */
export function ChatIgnoredList({
  users,
  emptyLabel,
  unignoreLabel,
  unignoreName,
  onUnignore,
}: Pick<
  ChatIgnoredModalProps,
  "users" | "emptyLabel" | "unignoreLabel" | "unignoreName" | "onUnignore"
>) {
  if (users.length === 0) {
    return (
      <Text variant="body-b2" tone="secondary">
        {emptyLabel}
      </Text>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {users.map((user) => (
        <li key={user.id} className="flex items-center gap-3">
          <Avatar name={user.name} size="sm" decorative />
          <Text variant="body-b2-strong" tone="primary" className="min-w-0 flex-1 truncate">
            {user.name}
          </Text>
          <Button
            variant="secondary"
            size="sm"
            aria-label={unignoreName(user.name)}
            onClick={() => onUnignore(user.id)}
          >
            {unignoreLabel}
          </Button>
        </li>
      ))}
    </ul>
  );
}

/** The players this reader hides in chat, each with a way to show them again. */
export function ChatIgnoredModal({
  open,
  onOpenChange,
  title,
  description,
  users,
  emptyLabel,
  unignoreLabel,
  unignoreName,
  onUnignore,
  doneLabel,
}: ChatIgnoredModalProps) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      footer={
        <ModalFooter>
          <ModalClose render={<Button />}>{doneLabel}</ModalClose>
        </ModalFooter>
      }
    >
      <ChatIgnoredList
        users={users}
        emptyLabel={emptyLabel}
        unignoreLabel={unignoreLabel}
        unignoreName={unignoreName}
        onUnignore={onUnignore}
      />
    </Modal>
  );
}
